import { useToast } from "@chakra-ui/react";
import CommentCard from "components/Organismos/CommentCard";
import CommentForm from "components/Organismos/CommentForm";
import { Map } from "components/Organismos/Map";
import ComplaintDetailTemplate from "components/Templates/ComplaintDetail";
import L, { LatLng } from "leaflet";
import { useAuth } from "providers/auth.provider";
import { useCallback, useEffect } from "react";
import { Marker, TileLayer } from "react-leaflet";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { getComplaintByIdRequest } from "store/modules/complaints/actions/getComplaintById";
import { sentCommentRequest } from "store/modules/complaints/actions/sentComment";
import { useQueryParam } from "utils/hooks/useQueryParam";
import { wait } from "utils/wait";
import complaintPin from '_assets/imgs/mapa/map-icon-red.png';

const complaintIcon = L.icon({
    iconUrl: complaintPin,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const ComplaintDetailsPage = () => {
    const { info } = useAuth();
    const [showDetailsComplaint, setShowDetailsComplaint] = useQueryParam<boolean>('showDetailsComplaint');
    const [complaintId, setComplaintId] = useQueryParam<string>('complaintId');

    const dispatch = useAppDispatch();
    const {
        successPayload: complaint,
        isRequesting
    } = useTypedSelector(x => x.complaints.getComplaintById);

    const fetchDetails = useCallback((complaintId: string) => {
        dispatch(getComplaintByIdRequest({
            complaintId
        }));
    }, [dispatch]);
    const toast = useToast();

    useEffect(() => {
        if (complaintId) {
            fetchDetails(complaintId);
        }
    }, [complaintId]);

    const sendComment = useCallback((texto: string) => {
        dispatch(sentCommentRequest({
            complaintId: complaintId!,
            texto,
            onSuccess: () => {
                toast({
                    status: 'success',
                    description: 'Resposta enviada com sucesso!',
                });
                fetchDetails(complaintId!);
            },
            onFailed: () => {
                toast({
                    status: 'error',
                    description: 'Houve um problema ao enviar sua resposta.',
                    containerStyle: {
                        zIndex: 9999999
                    }
                })
            }
        }))
    }, [complaintId, dispatch])

    if (!complaint) {
        return null;
    }

    return (
        <ComplaintDetailTemplate
            loading={isRequesting && !!complaint}
            title={complaint.titulo}
            photo={`${process.env.REACT_APP_API}/manifestacoes/imagem/${complaint.idManifestacao}`}
            protocol={complaint.protocolo}
            description={complaint.descricao}
            complaintType={complaint.tipoManifestacao}
            status={complaint.status}
            createdDate={new Date(`${complaint.dataAbertura}T03:00:00`)}
            userInformation={{
                name: complaint.nomeUsuario,
                email: complaint.emailUsuario,
                cpf: complaint.cpfUsuario,
                phoneNumber: complaint.telefoneUsuario,
                address: `${complaint.enderecoUsuario.logradouro}, ${complaint.enderecoUsuario.numero} - Bairro: ${complaint.enderecoUsuario.bairro}, ${complaint.enderecoUsuario.municipio}/${complaint.enderecoUsuario.uf}`
            }}
            isOpenModal={String(showDetailsComplaint) === 'true'}
            onCloseModal={async () => {
                setShowDetailsComplaint(false);
                await wait(200);
                setComplaintId('');
            }}
            map={(
                <Map
                    zoom={14}
                    center={new LatLng(complaint.latitude, complaint.longitude)}
                    dragging={false}
                    zoomControl={false}
                    scrollWheelZoom={false}
                    doubleClickZoom={false}
                    style={{ width: '100%', height: 200 }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker icon={complaintIcon} position={new LatLng(complaint.latitude, complaint.longitude)} />
                </Map>
            )}
            messages={
                <>
                    {complaint?.mensagens.map(message => (
                        <CommentCard
                            comment={message.texto}
                            senderName={message.usuario}
                            sentDate={message.horario}
                            isMine={message.usuario === info?.name}
                        />
                    ))}

                    <CommentForm
                        isFinished={complaint?.status === "Encerrada"}
                        onSuccess={({ comment }) => sendComment(comment)}
                    />
                </>
            }
        />
    )
}
export default ComplaintDetailsPage